import {
    Dialog,
    DialogBody,
    HTMLTable,
    Tab,
    TabId,
    Tabs,
    Tag
} from "@blueprintjs/core";
import useMapViewConfiguration from "contexts/MapViewConfigurationContext/hooks/useMapViewConfiguration";
import { loader } from "graphql.macro";
import { getHasuraDataAndConvertToNiraType } from "graphql/helpers/queryHelpers";
import { useState } from "react";
import {
    AdvancedStudy,
    convertHasuraAdvancedStudiesToNiraAdvancedStudies,
    STATUS_TO_DISPLAY_STRING,
    STATUS_TO_INTENT
} from "types/advancedStudyType";
import { Bus } from "types/busType";
import { Loading, mapLoadingState } from "types/loadingType";
import "./ViewAdvancedStudiesDialog.scss";
import ViewAdvancedStudyResults from "./ViewAdvancedStudyResults";

const GET_ADVANCED_STUDIES = loader("src/graphql/getAdvancedStudies.graphql");

interface ViewAdvancedStudiesDialogProps {
    isOpen: boolean;
    onClose: () => void;
    selectedBus?: Bus;
}

const ViewAdvancedStudiesDialog: React.FC<ViewAdvancedStudiesDialogProps> = (
    props
) => {
    const { isOpen, onClose, selectedBus } = props;
    const {
        busFiltersConfiguration: {
            busFilters: { scope }
        }
    } = useMapViewConfiguration();

    const [selectedTabId, setSelectedTabId] = useState<TabId>(
        selectedBus === undefined ? "all" : "bus"
    );
    const [selectedAdvancedStudy, setSelectedAdvancedStudy] = useState<
        AdvancedStudy | undefined
    >(undefined);

    const allAdvancedStudies = getHasuraDataAndConvertToNiraType(
        GET_ADVANCED_STUDIES,
        convertHasuraAdvancedStudiesToNiraAdvancedStudies,
        { scope },
        !isOpen
    );

    const selectedBusStudies = mapLoadingState(allAdvancedStudies, (studies) =>
        studies.filter((study) =>
            study.loads.some(
                (load) => load.niraBusId === selectedBus?.niraBusId
            )
        )
    );

    if (!isOpen) {
        return null;
    }

    const renderStudiesTable = (
        studies: Loading<ReadonlyArray<AdvancedStudy>>
    ) => {
        // TODO: Improve loading state UI
        if (studies == "loading") {
            return <div>Loading...</div>;
        }
        return (
            <HTMLTable
                className="ViewAdvancedStudiesDialog-table"
                striped
                interactive
            >
                <thead>
                    <tr>
                        <th>Title</th>
                        <th>Status</th>
                        <th>Loads</th>
                    </tr>
                </thead>
                <tbody>
                    {studies.map((study) => (
                        <tr
                            key={study.advancedStudyId}
                            onClick={() => setSelectedAdvancedStudy(study)}
                            style={{ cursor: "pointer" }}
                        >
                            <td>{study.title}</td>
                            <td>
                                <Tag intent={STATUS_TO_INTENT[study.status]}>
                                    {STATUS_TO_DISPLAY_STRING[study.status]}
                                </Tag>
                            </td>
                            <td>
                                <div className="ViewAdvancedStudiesDialog-loads">
                                    {study.loads.map((load) => (
                                        <Tag key={load.niraBusId} minimal>
                                            {load.busDisplayName}
                                            {load.benchMwSize !== undefined &&
                                                ` (Bench: ${load.benchMwSize} MW)`}
                                            {load.studyMwSize !== undefined &&
                                                ` (Study: ${load.studyMwSize} MW)`}
                                        </Tag>
                                    ))}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </HTMLTable>
        );
    };

    const tabs = (
        <Tabs
            selectedTabId={selectedTabId}
            onChange={(newTabId) => setSelectedTabId(newTabId)}
        >
            <Tab
                id="all"
                title="All Advanced Studies"
                panel={renderStudiesTable(allAdvancedStudies)}
            />
            {selectedBus !== undefined && (
                <Tab
                    id="bus"
                    title={`${selectedBus.busDisplayName} Advanced Studies`}
                    panel={renderStudiesTable(selectedBusStudies)}
                />
            )}
        </Tabs>
    );

    const dialogBody = selectedAdvancedStudy ? (
        <ViewAdvancedStudyResults
            advancedStudy={selectedAdvancedStudy}
            onBack={() => setSelectedAdvancedStudy(undefined)}
        />
    ) : (
        tabs
    );

    return (
        <Dialog
            className="ViewAdvancedStudiesDialog"
            isOpen={isOpen}
            onClose={onClose}
            title="View Advanced Studies"
        >
            <DialogBody>{dialogBody}</DialogBody>
        </Dialog>
    );
};

export default ViewAdvancedStudiesDialog;
